export class LineNotify {

  /** @type {import('../HTTPClient').HTTPClient} */
  http

  /**
   * LINE Notify Service
   * 
   * @param {import('../HTTPClient').HTTPClient} http 
   * @param {String} backend 
   */
  constructor(http, backend) {
    this.http = http
    this.url = `${backend}/line-notify`    
  }

  /**
   * @typedef Result
   * @type {Object}
   * @property {Number} code - Result Code
   * @property {String} message - Result Message
   */

  /**
   * @typedef ListResult
   * @type {Object}
   * @property {Number} total - Total Count
   * @property {Array} items - Data List
   */

  /**
   * Fetch Notice List
   * 
   * @param {String} search - Search Text
   * @param {Number} page - Fetch Page
   * @param {Number} limit - Page Limitation
   * @returns {Promise<Result & ListResult>} Notice List
   */
  async noticeList (search, page, limit) {

    const searchText = search || ''
    const pageNum = parseInt(page, 10) || 1
    const limitNum = parseInt(limit, 10) || 10

    const api = `${this.url}/list-notice`
    const params = {
      search: searchText, page: pageNum, limit: limitNum
    }

    const response = await this.http.request(
      'get', api, { params }
    )

    let code = 50000
    let message = LineNotify.messages(code)
    let data = {
      total: 0, items: []
    }

    if (response.status === 200) {

      const { result } = response.data

      code = result.code
      data = result.data
      
      message = LineNotify.messages(code)
    }

    return {
      code, message, ...data
    }
  }

  /**
   * Fetch Admin List
   * 
   * @param {String} search - Search Text
   * @param {Number} page - Fetch Page
   * @param {Number} limit - Page Limitation
   * @returns {Promise<Result & ListResult>} Admin List
   */
  async adminList (search, page, limit) {

    const searchText = search || ''
    const pageNum = parseInt(page, 10) || 1
    const limitNum = parseInt(limit, 10) || 10

    const api = `${this.url}/list-admin`
    const params = {
      search: searchText, page: pageNum, limit: limitNum
    }

    const response = await this.http.request(
      'get', api, { params }
    )

    let code = 50000
    let message = LineNotify.messages(code)
    let data = {
      total: 0, items: []
    }

    if (response.status === 200) {

      const { result } = response.data

      code = result.code
      data = result.data

      message = LineNotify.messages(code)
    }

    return {
      code, message, ...data
    }
  }

  /**
   * Fetch Project List
   * 
   * @param {String} search - Search Text
   * @param {Number} page - Fetch Page
   * @param {Number} limit - Page Limitation
   * @returns {Promise<Result & ListResult>} Project List
   */
  async projectList (search, page, limit) {

    const searchText = search || ''
    const pageNum = parseInt(page, 10) || 1
    const limitNum = parseInt(limit, 10) || 10

    const api = `${this.url}/list-project`

    const params = {
      search: searchText, page: pageNum, limit: limitNum
    }

    const response = await this.http.request(
      'get', api, { params }
    )

    let code = 50000
    let message = LineNotify.messages(code)
    let data = {
      total: 0, items: []
    }

    if (response.status === 200) {

      const { result } = response.data

      code = result.code
      data = result.data
      
      message = LineNotify.messages(code)
    }

    return {
      code, message, ...data
    }
  }

  /**
   * @typedef CreateUser
   * @type {Object}
   * @property {String} lineId - LINE UID
   * @property {String} ruejaiId - User UID
   * @property {String} email - User Email
   * @property {String} firstName - First Name
   * @property {String} lastName - Last Name
   * @property {String} roleName - User Role Name
   * @property {String[]} projects - Notice Project IDs
   */

  /**
   * @typedef CreateResult
   * @type {Object}
   * @property {Number} id
   */

  /**
   * Create LINE Notify User
   * 
   * @param {CreateUser} params - LINE User Data
   * @returns {Promise<Result & CreateResult>} - Created Result
   */
  async addUser (params) {
    
    const api = `${this.url}/add-user`

    const payload = { ...params }

    const response = await this.http.request(
      'post', api, { data: payload }
    )

    let code = 50000
    let message = LineNotify.messages(code)
    let data = 0

    if (response.status === 200) {

      const { result } = response.data

      code = result.code
      data = result.data

      message = LineNotify.messages(code)
    }

    return {
      code, message, data
    }
  }


  /**
   * @typedef NoticeResult
   * @type {Object}
   * @property {Number} id - Notice ID
   * @property {String} projectId - Project ID
   */

  /**
   * @typedef AddNoticeResult
   * @type {Object}
   * @property {NoticeResult[]} data
   */

  /**
   * Add Notices
   * 
   * @param {Number} id 
   * @returns {Promise<Result & AddNoticeResult>}
   */
  async addNotice (ruejaiId, project) {
    
    const api = `${this.url}/add-notice`

    const payload = { ruejaiId, project }

    const response = await this.http.request(
      'post', api, { data: payload }
    )

    let code = 50000
    let message = LineNotify.messages(code)
    let data = []

    if (response.status === 200) {

      const { result } = response.data

      code = result.code
      data = result.data

      message = LineNotify.messages(code)
    }

    return {
      code, message, data
    }
  }

  /**
   * @typedef RemoveResult
   * @type {Object}
   * @property {Number} affected - delete affected rows
   */

  /**
   * Remove LINE User by ID
   * 
   * @param {String} id - Ruejai ID (Admin ID)
   * @returns {Promise<Result & RemoveResult>} Soft Deleted Result
   */
  async removeUser (id) {

    const api = `${this.url}/remove-user/${id}`

    const response = await this.http.request(
      'delete', api
    )

    let code = 50000
    let message = LineNotify.messages(code)
    let data = 0

    if (response.status === 200) {

      const { result } = response.data

      code = result.code
      data = result.data

      message = LineNotify.messages(code)
    }

    return {
      code, message, data
    }
  }

  /**
   * Remove Notice by ID
   * 
   * @param {Number} id - Notice ID
   * @returns {Promise<Result & RemoveResult>} Soft Deleted Result
   */
  async removeNotice (id) {

    const api = `${this.url}/remove-notice/${id}`

    const response = await this.http.request(
      'delete', api
    )

    let code = 50000
    let message = LineNotify.messages(code)
    let data = 0

    if (response.status === 200) {

      const { result } = response.data

      code = result.code
      data = result.data

      message = LineNotify.messages(code)
    }

    return {
      code, message, data
    }
  }
  
  /**
   * Set Description
   * 
   * @param {Number} code 
   * @returns {String} Result Description
   */
  static messages (code) {

    const messages = {
      20000: 'เรียบร้อย',
      40000: 'โปรดกรอกข้อมูลให้ครบถ้วน',
      40400: 'ไม่พบข้อมูล',
      40900: 'มีข้อมูลอยู่แล้ว',
      50000: 'เกิดข้อผิดพลาด โปรดลองอีกครั้งในภายหลัง'
    }
    
    return messages[code || 50000]
  }
}
